import type { FC, HTMLAttributes, ReactNode } from "react";
import { cn } from "../utilities/cn";
import {
  type Align,
  type Gap,
  type Justify,
  alignClass,
  gapClass,
  justifyClass,
} from "../utilities/layout";

export interface InlineProps extends HTMLAttributes<HTMLDivElement> {
  /** Horizontal gap between children (Tailwind `gap-*` scale). Defaults to 2. */
  gap?: Gap;
  /** Cross-axis (vertical) alignment of children. Defaults to "center". */
  align?: Align;
  /** Main-axis distribution of children. */
  justify?: Justify;
  /** Allow children to wrap onto multiple lines. */
  wrap?: boolean;
  children: ReactNode;
}

/**
 * Horizontal flex row with a token-based gap, vertically centered by default.
 * Replaces repeated `flex items-center gap-*` and
 * `flex justify-between items-center gap-*` wrappers.
 */
export const Inline: FC<InlineProps> = ({
  gap = 2,
  align = "center",
  justify,
  wrap = false,
  className,
  children,
  ...rest
}) => (
  <div
    className={cn(
      "flex",
      alignClass[align],
      justify && justifyClass[justify],
      wrap && "flex-wrap",
      gapClass[gap],
      className,
    )}
    {...rest}
  >
    {children}
  </div>
);
